import React from 'react';
import TextField from '@mui/material/TextField';

// Import avec vérification de disponibilité
const Autocomplete = React.lazy(() => import('@mui/material/Autocomplete'));

interface SelectFieldValue{
	value: string;
	label: string;
}

interface SelectFieldProps {
	name: string;
	value: SelectFieldValue;
	onChange: (value: SelectFieldValue) => void;
	options: SelectFieldValue[];
	label: string;
	required?: boolean;
	disabled?: boolean;
	title: string;
	error?: string;
	functionValidator: (value: SelectFieldValue) => { result: boolean; message: string };
	placeholder?: string;
}

const SelectField = ({
	name,
	value,
	onChange,
	options,
	label,
	required,
	disabled,
	title,
	error,
	functionValidator,
	placeholder,
	...rest
} : SelectFieldProps ) => {
	// S'assurer que value est un objet valide avec useMemo pour éviter les recalculs inutiles
	const safeValue = React.useMemo(() => {
		return value || { value: '', label: '' };
	}, [value]);
	
	/**
	 * Validateur du champs
	 * @param {Object} newValue
	 */
	const fieldValidator = React.useCallback((newValue : SelectFieldValue) => {
		const valueValidated = {
			label: newValue?.label,
			value: newValue?.value,
			error: '',
			isValid: true,
		};

		if (newValue == null) {
			valueValidated.isValid = false;
		}

		if (required && !newValue) {
			valueValidated.isValid = false;
			valueValidated.error = `Champ requis`;
		}

		// on test la props fonction de validation si nécessaire
		if (valueValidated.isValid && functionValidator) {
			const customValidator = functionValidator(newValue);
			if (customValidator?.result === false) {
				valueValidated.isValid = false;
				valueValidated.error = customValidator.message || 'Format spécifique non valide';
			}
		}
		onChange(valueValidated);
	}, [required, functionValidator, onChange])

	React.useEffect(() => {
		if (safeValue && safeValue.value && safeValue.value.length) {
			fieldValidator(safeValue);
		}
	}, [fieldValidator, safeValue]);
	
	// Vérifier que tous les éléments nécessaires sont disponibles - après les hooks
	if (!name || !onChange || !options) {
		console.error('SelectField: Required props missing', { name, onChange, options });
		return null;
	}
	
	return (
		<Autocomplete
			data-testid={`autocomplete_${name}`}
			isOptionEqualToValue={(option: unknown, value: unknown) => {
				// Vérifier si option et value sont des objets
				if (!option || !value || typeof option !== 'object' || typeof value !== 'object') return false;
				
				// Vérifier si les propriétés 'value' existent
				const opt = option as any;
				const val = value as any;
				
				if (!('value' in opt) || !('value' in val)) return false;
				
				// Maintenant nous pouvons accéder aux propriétés en toute sécurité
				const isEqual = opt.value === val.value;
				
				if (!val.value || val.value === '') {
					return true;
				} else {
					return isEqual;
				}
			}}
			disableClearable
			fullWidth
			title={title}
			disabled={disabled}
			renderInput={({ inputProps, ...params }) => (
				<TextField
					{...params}
					label={label}
					size="small"
					margin="none"
					fullWidth
					required={required}
					helperText={error}
					error={!!error}
					variant="outlined"
					placeholder={placeholder}
					inputProps={{
						'data-testid': 'textSelectSingleInput',
						...inputProps,
					}}
				/>
			)}
			noOptionsText="Aucune option"
			options={options}
			onChange={ (e, newValue) => {fieldValidator(newValue as SelectFieldValue)}}
			value={safeValue}
			autoHighlight
			{...rest}
		/>
	);
};

// Envelopper avec ErrorBoundary pour capturer les erreurs 
// et avec React.memo pour éviter les rendus inutiles
const MemoizedSelectField = React.memo(SelectField);

// Wrapper sécurisé pour gérer les erreurs et les cas problématiques
const SafeSelectField = (props: SelectFieldProps) => {
	try {
		// Utiliser Suspense pour gérer le chargement lazy des imports
		return (
			<React.Suspense fallback={<div>Chargement...</div>}>
				<MemoizedSelectField {...props} />
			</React.Suspense>
		);
	} catch (error) {
		console.error('Error rendering SelectField:', error);
		return <div>Erreur de chargement du composant</div>;
	}
};

export default SafeSelectField;
